// components/EventTeaser.jsx
import React from 'react';

const EventTeaser = ({ program, lang = 'en', isLast = false }) => {
  const topicName = program.__topicName || '';
  const typeLabel = program.programType?.type || ''; // Talk / Advice / Tour / More
  const startTime = new Date(program.start_time);
  const endTime = new Date(program.end_time);

  const startTimeStr = startTime.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });
  const endTimeStr = endTime.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
  });

  const timeLabel = `${startTimeStr}-${endTimeStr}`;

  const mainTitle =
    program.title ||
    program.description_short ||
    'Untitled event';

  const longDescription =
    program.description ||
    program.description_short ||
    '';

  const locationTitle = program.location?.title;
  const campusTitle = program.location?.campus?.title;
  const locationWebsite = program.location?.website;
  const room = (program.room || '').trim();
  const floor = (program.floor || '').trim();
  const roomOrFloor = room || floor;
  const locationWithCampus = locationTitle
    ? `${locationTitle}${campusTitle ? ` (${campusTitle})` : ''}`
    : '';
  const isAccessible = Boolean(program.location?.accessible);
  const hasBikeParking = Boolean(program.location?.bike_parking);
  const hasAccessibilityInfo =
    program.location?.accessible !== undefined &&
    program.location?.accessible !== null;
  const hasBikeInfo =
    program.location?.bike_parking !== undefined &&
    program.location?.bike_parking !== null;

  return (
    <div
      className="teaser teaser-event"
      data-event={program.id}
      style={isLast ? { borderBottom: 'none' } : {}}
    >
      {/* Single column – body only */}
      <div className="teaser-body">
        <h2 className="teaser-title">
          {mainTitle}
        </h2>

        <div className="teaser-details">
          <div className="teaser-details-row teaser-details-row--time">
            <span className="teaser-details-item">
              <svg
                className="icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 32 32"
              >
                <title>{lang === 'cy' ? 'Amser' : 'Time'}</title>
                <path d="M16,0A16,16,0,0,1,27.34,27.31a16,16,0,0,1-22.66,0,16,16,0,0,1,0-22.66A15.86,15.86,0,0,1,16,0Zm0,28A12,12,0,0,0,24.5,7.5a12,12,0,1,0-17,17A11.82,11.82,0,0,0,16,28Zm2-12.81V8L14,8v8.81l6.59,6.59,2.81-2.84Z" />
              </svg>
              <span>{timeLabel}</span>
            </span>

            {(hasAccessibilityInfo || hasBikeInfo) && (
              <span className="location-amenities" aria-label="Accessibility and bike parking">
                {hasAccessibilityInfo && (
                  <span
                    className={`amenity-icon ${
                      isAccessible ? 'amenity--ok' : 'amenity--no'
                    }`}
                    title={isAccessible ? (lang === 'cy' ? 'Yn hygyrch' : 'Accessible') : (lang === 'cy' ? 'Hygyrchedd cyfyngedig' : 'Limited accessibility')}
                    aria-label={isAccessible ? (lang === 'cy' ? 'Yn hygyrch' : 'Accessible') : (lang === 'cy' ? 'Hygyrchedd cyfyngedig' : 'Limited accessibility')}
                  >
                    <svg
                      className="icon"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 640 640"
                      aria-hidden="true"
                    >
                      <path d="M200 104C200 73.1 225.1 48 256 48C286.9 48 312 73.1 312 104C312 134.9 286.9 160 256 160C225.1 160 200 134.9 200 104zM181.9 261.6L194.5 324.6C155.9 337 128 373.3 128 416C128 469 171 512 224 512C259.6 512 290.7 492.6 307.3 463.8C309.6 463.9 311.9 464 314.3 464L318.4 464C319.4 464 320.5 464 321.5 464L376.7 464C356.3 528.9 295.7 576 224 576C135.6 576 64 504.4 64 416C64 342.2 114 280.1 181.9 261.6zM330.5 233.5L354.2 352L414.7 352C448 352 477.9 372.7 489.6 403.9L515.1 471.9L533.8 465.7C550.6 460.1 568.7 469.2 574.3 485.9C579.9 502.6 570.8 520.8 554.1 526.4L506.1 542.4C489.8 547.8 472.1 539.4 466 523.3L429.7 426.4C427.4 420.2 421.4 416 414.7 416L328.6 416C328.2 416 327.8 416 327.3 416L314.2 416C283.7 416 257.4 394.5 251.4 364.6L229.3 253.7C222.9 221.8 247.3 192 279.9 192C304.5 192 325.7 209.4 330.5 233.5z" />
                    </svg>
                    {!isAccessible && (
                      <span className="amenity-x" aria-hidden="true">×</span>
                    )}
                  </span>
                )}

                {hasBikeInfo && (
                  <span
                    className={`amenity-icon ${
                      hasBikeParking ? 'amenity--ok' : 'amenity--no'
                    }`}
                    title={hasBikeParking ? (lang === 'cy' ? 'Parcio beiciau' : 'Bike parking') : (lang === 'cy' ? 'Dim parcio beiciau' : 'No bike parking')}
                    aria-label={hasBikeParking ? (lang === 'cy' ? 'Parcio beiciau' : 'Bike parking') : (lang === 'cy' ? 'Dim parcio beiciau' : 'No bike parking')}
                  >
                    <svg
                      className="icon"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 640 640"
                      aria-hidden="true"
                    >
                      <path d="M400 160C426.5 160 448 138.5 448 112C448 85.5 426.5 64 400 64C373.5 64 352 85.5 352 112C352 138.5 373.5 160 400 160zM427.2 224L365.4 175.2C348.1 161.6 323.7 161.4 306.3 174.9L223.2 239.1C192.5 262.9 194.7 309.9 227.5 330.7L288 369.1L288 480C288 497.7 302.3 512 320 512C337.7 512 352 497.7 352 480L352 352C352 341.3 346.7 331.3 337.8 325.4L295 296.9L355.3 248.4L396 281C401.7 285.5 408.7 288 416 288L480 288C497.7 288 512 273.7 512 256C512 238.3 497.7 224 480 224L427.2 224zM144 576C205.9 576 256 525.9 256 464C256 402.1 205.9 352 144 352C82.1 352 32 402.1 32 464C32 525.9 82.1 576 144 576zM496 576C557.9 576 608 525.9 608 464C608 402.1 557.9 352 496 352C434.1 352 384 402.1 384 464C384 525.9 434.1 576 496 576z" />
                    </svg>
                    {!hasBikeParking && (
                      <span className="amenity-x" aria-hidden="true">×</span>
                    )}
                  </span>
                )}
              </span>
            )}
          </div>

          {(campusTitle || locationTitle || roomOrFloor) && (
            <div className="teaser-details-row teaser-details-row--location">
              <span className="teaser-details-item teaser-details-item--location">
                <svg
                  className="icon"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 640 640"
                >
                  <title>{lang === 'cy' ? 'Lleoliad' : 'Location'}</title>
                  <path d="M302.7 69.1C313.2 62.3 326.8 62.3 337.3 69.1L561.3 213.1C573.2 220.8 578.7 235.4 574.7 249C570.7 262.6 558.2 272 544 272L512 272L512 480L563.2 518.4C571.3 524.4 576 533.9 576 544C576 561.7 561.7 576 544 576L96 576C78.3 576 64 561.7 64 544C64 533.9 68.7 524.4 76.8 518.4L128 480L128 480L128 272L96 272C81.8 272 69.3 262.6 65.3 249C61.3 235.4 66.8 220.7 78.7 213.1L302.7 69.1zM400 272L400 480L464 480L464 272L400 272zM288 480L352 480L352 272L288 272L288 480zM176 272L176 480L240 480L240 272L176 272z"/>
                </svg>
                {locationTitle ? (
                  locationWebsite ? (
                    <a
                      href={locationWebsite}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'underline', color: 'inherit' }}
                    >
                      {roomOrFloor ? `${locationWithCampus}:` : locationWithCampus}
                    </a>
                  ) : (
                    <span>{roomOrFloor ? `${locationWithCampus}:` : locationWithCampus}</span>
                  )
                ) : null}
                {!locationTitle && campusTitle ? (
                  <span>{roomOrFloor ? `${campusTitle}:` : campusTitle}</span>
                ) : null}
                {roomOrFloor ? <span>{` ${roomOrFloor}`}</span> : null}
              </span>
            </div>
          )}
        </div>

        {/* Description */}
        {longDescription && (
          <p className="mt-10">
            {longDescription}
          </p>
        )}

        {/* Topic and Type badges */}
        {(topicName || typeLabel) && (
          <ul className="list list-horizontal mt-10 m-0">
            {/* Topic badge */}
            {topicName && (
              <li className="mr-1">
                <span className="badge">
                  {topicName}
                </span>
              </li>
            )}
            {/* Type badge */}
            {typeLabel && (
              <li>
                <span className="badge">
                  {typeLabel}
                </span>
              </li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

export default EventTeaser;
